import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { KeyRound, Loader2, ArrowLeft, ExternalLink } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [resetUrl, setResetUrl] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setSent(true);
      if (data?.reset_url) setResetUrl(data.reset_url);
      toast.success("Check your inbox — link also shown below");
    } catch { toast.error("Something went wrong"); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#090D14] grid place-items-center text-white p-4">
      <div className="w-full max-w-md">
        <Link to="/admin/login" className="text-slate-400 hover:text-amber-400 text-sm flex items-center gap-2 mb-6"><ArrowLeft className="w-4 h-4" /> Back to sign in</Link>
        <div className="glass-card rounded-2xl p-8 gold-border">
          <div className="text-caption mb-2 flex items-center gap-1.5"><KeyRound className="w-3.5 h-3.5" /> Password recovery</div>
          <h1 className="font-display text-2xl font-bold">Forgot your password?</h1>
          <p className="text-sm text-slate-400 mt-1">We'll email you a secure link to set a new one — and show it below so you can proceed straight away.</p>
          {sent ? (
            <div className="mt-6 space-y-4" data-testid="forgot-sent">
              <div className="rounded-lg bg-emerald-500/10 border border-emerald-400/30 p-4 text-sm text-emerald-200">
                If <span className="font-mono">{email}</span> matches an admin account, a reset link is on its way. It expires in 30 minutes.
              </div>
              {resetUrl && (
                <div className="rounded-lg bg-amber-400/10 border border-amber-400/30 p-4 space-y-3">
                  <div className="text-xs uppercase tracking-widest font-mono text-amber-400/90">Instant reset link</div>
                  <p className="text-xs text-slate-300">Email delayed or in spam? Use this direct link — same 30-minute expiry.</p>
                  <a href={resetUrl} data-testid="forgot-reset-link">
                    <Button className="btn-gold w-full rounded-lg h-10">
                      <ExternalLink className="w-3.5 h-3.5 mr-2" /> Reset my password now
                    </Button>
                  </a>
                  <button
                    onClick={() => { navigator.clipboard.writeText(resetUrl); toast.success("Link copied"); }}
                    className="text-xs text-slate-400 hover:text-amber-400 w-full text-center"
                  >Or copy link</button>
                </div>
              )}
            </div>
          ) : (
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div>
                <Label className="text-xs uppercase text-slate-400 tracking-wider">Email</Label>
                <Input data-testid="forgot-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="bg-black/30 border-white/10 h-11 mt-2" placeholder="you@apextaxi.co.nz" />
              </div>
              <Button data-testid="forgot-submit" type="submit" disabled={loading} className="w-full btn-gold rounded-lg h-11">
                {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Send reset link
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
